import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Wrench, ClipboardCheck, ShieldCheck, PackageOpen, KeyRound, Siren,
  ParkingSquare, BellRing, TrendingDown, HardHat, FileText, Sparkles,
  UserCircle2, PawPrint, BadgeCheck, DollarSign, UploadCloud, Server, Settings2,
  ChevronsUpDown, LogOut, ChevronRight, ChevronDown, Bot, ClipboardList, ScanEye,
  Building2, LayoutDashboard, Archive, Phone, Briefcase, KeySquare, Vote,
  Users, Users2, Search, X, LifeBuoy, Landmark, Home, Command,
  Scale, Gavel, TrendingUp, HeartPulse, Library, Coins, BookOpen, MessageSquare,
  DoorOpen, UserPlus, Zap, Trophy, Mail, TestTube,
} from "lucide-react";
import PropertySwitcher from "@/components/PropertySwitcher";
import AIConciergeDrawer from "@/components/AIConciergeDrawer";
import { LiveIndicator, LiveNotificationsBell } from "@/components/LiveIndicator";
import { NotificationDrawer } from "@/components/NotificationDrawer";
import CommandPalette from "@/components/CommandPalette";
import ThemeToggle from "@/components/ThemeToggle";
import { filterGroupsByFeatures } from "@/lib/featureRouteMap";
import { api } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * Sidebar taxonomy — 24 admin operations items grouped by domain so directors
 * scan by intent, not by memorizing a 24-row flat list. Search across every
 * group. State (collapsed/open) persisted in localStorage.
 */
const NAV_GROUPS = [
  {
    id: "overview", title: "Overview", groupIcon: Home,
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/portfolio", label: "Portfolio Rollup", icon: TrendingUp },
    ],
  },
  {
    id: "community", title: "Community", groupIcon: Users,
    items: [
      { to: "/admin/communities", label: "Communities", icon: Building2 },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/onboarding/batches", label: "Onboarding Wizard", icon: UserPlus },
      { to: "/admin/permission-templates", label: "Permission Templates", icon: ShieldCheck },
      { to: "/admin/committees", label: "Committees", icon: Users2 },
      { to: "/admin/residents", label: "Residents", icon: UserCircle2 },
      { to: "/admin/units-management", label: "Units & Occupants", icon: DoorOpen },
    ],
  },
  {
    id: "financials", title: "Financials", groupIcon: Landmark,
    items: [
      { to: "/admin/finance", label: "Finance Dashboard", icon: DollarSign },
      { to: "/admin/dues", label: "Dues Schedule", icon: DollarSign },
      { to: "/admin/stripe-walkthrough", label: "Stripe Test Walkthrough", icon: TestTube },
      { to: "/admin/estoppel", label: "Estoppel Certificates", icon: ClipboardCheck },
      { to: "/admin/cam", label: "CAM Reconciliation", icon: Landmark },
      { to: "/admin/coa", label: "Chart of Accounts", icon: BookOpen },
      { to: "/admin/gl", label: "General Ledger", icon: DollarSign },
      { to: "/admin/journal-entries", label: "Journal Entries", icon: Scale },
      { to: "/admin/leases", label: "Rent Roll & Leases", icon: DollarSign },
      { to: "/admin/bank-recon", label: "Bank Reconciliation", icon: DollarSign },
      { to: "/admin/trust-accounts", label: "Trust Accounts", icon: ShieldCheck },
      { to: "/admin/owners", label: "Owner Portal", icon: DollarSign },
      { to: "/admin/ap", label: "Accounts Payable", icon: DollarSign },
      { to: "/admin/payments", label: "Payments", icon: DollarSign },
      { to: "/admin/delinquency", label: "Delinquency Pipeline", icon: TrendingDown },
      { to: "/admin/delinquency-waterfall", label: "Delinquency Waterfall", icon: TrendingDown },
      { to: "/admin/delinquency-letters", label: "Letter Templates", icon: Scale },
      { to: "/admin/special-assessments", label: "Special Assessments", icon: Landmark },
      { to: "/admin/reserve-study", label: "Reserve Study", icon: DollarSign },
      { to: "/admin/tax/1099", label: "1099-NEC (Vendors)", icon: DollarSign },
      { to: "/admin/tax/1099-misc", label: "1099-MISC (Owners)", icon: DollarSign },
    ],
  },
  {
    id: "leasing", title: "Leasing", groupIcon: Home,
    items: [
      { to: "/admin/leasing", label: "Listings & Applications", icon: Home },
      { to: "/admin/screening", label: "Renter Screening", icon: ScanEye },
      { to: "/admin/vacancy", label: "Vacancy Tracker", icon: TrendingDown },
    ],
  },
  {
    id: "operations", title: "Operations", groupIcon: Wrench,
    items: [
      { to: "/admin/maintenance", label: "Maintenance", icon: Wrench },
      { to: "/admin/tech-leaderboard", label: "Tech Leaderboard", icon: Trophy },
      { to: "/admin/oncall", label: "On-Call Rotation", icon: Zap },
      { to: "/admin/pm-schedule", label: "Preventive Maintenance", icon: Wrench },
      { to: "/admin/inspections", label: "Inspections", icon: ClipboardCheck },
      { to: "/admin/unit-turnover", label: "Move-in / Move-out", icon: ClipboardCheck },
      { to: "/admin/move-in-checklist", label: "Move-in Checklist", icon: Sparkles },
      { to: "/admin/move-out-wizard", label: "Move-out Wizard", icon: DoorOpen },
      { to: "/admin/insurance", label: "Insurance Tracking", icon: ShieldCheck },
      { to: "/admin/renter-insurance", label: "Renters Insurance", icon: ShieldCheck },
      { to: "/admin/packages", label: "Package Management", icon: PackageOpen },
      { to: "/admin/access", label: "Access Control", icon: KeyRound },
      { to: "/admin/emergency", label: "Emergency & Alerts", icon: Siren },
      { to: "/admin/parking", label: "Parking & Mailboxes", icon: ParkingSquare },
    ],
  },
  {
    id: "amenities", title: "Amenities & Life", groupIcon: LifeBuoy,
    items: [
      { to: "/admin/amenities", label: "Amenities", icon: Building2 },
      { to: "/admin/lost-found", label: "Lost & Found", icon: PackageOpen },
      { to: "/admin/pets", label: "Pet Registry", icon: PawPrint },
    ],
  },
  {
    id: "governance", title: "Governance & Records", groupIcon: Vote,
    items: [
      { to: "/admin/board-meetings", label: "Board Meetings", icon: Vote },
      { to: "/admin/board-terms", label: "Board Terms", icon: Users },
      { to: "/admin/board-elections", label: "Board Elections", icon: Vote },
      { to: "/admin/voting", label: "E-Voting & Proxies", icon: Vote },
      { to: "/admin/arc", label: "ARC Requests", icon: ClipboardList },
      { to: "/admin/violations", label: "Violations", icon: ClipboardList },
      { to: "/admin/documents", label: "Documents", icon: FileText },
      { to: "/admin/notifications", label: "Notifications", icon: BellRing },
      { to: "/admin/sms-inbox", label: "SMS Inbox", icon: BellRing },
    ],
  },
  {
    id: "vendors", title: "Vendors", groupIcon: HardHat,
    items: [
      { to: "/admin/vendors", label: "Vendors", icon: HardHat },
      { to: "/admin/vendor-compliance", label: "Compliance Tracker", icon: ShieldCheck },
    ],
  },
];

const AI_GROUP = {
  id: "ai", title: "AI Features", groupIcon: Sparkles, accent: "indigo",
  items: [
    { to: "/admin/ai/hub", label: "Resident AI Hub", icon: Sparkles },
    { to: "/admin/ai/avatar", label: "AI Avatar Concierge", icon: Bot },
    { to: "/admin/ai/concierge", label: "AI Concierge", icon: Bot },
    { to: "/admin/ai/maintenance", label: "Maintenance AI", icon: Wrench },
    { to: "/admin/ai/payment-advisor", label: "Payment Advisor", icon: DollarSign },
    { to: "/admin/ai/arc", label: "ARC AI Assistant", icon: ClipboardList },
    { to: "/admin/ai/violation", label: "Violation Pre-Check", icon: ScanEye },
    { to: "/admin/ai/disputes", label: "Dispute Resolution Wizard", icon: Scale },
    { to: "/admin/ai/vendor-negotiator", label: "Vendor Negotiator", icon: Gavel },
    { to: "/admin/ai/real-estate", label: "Real Estate Predictor", icon: TrendingUp },
    { to: "/admin/ai/forecast", label: "Financial Forecast", icon: TrendingUp },
    { to: "/admin/ai/sentiment", label: "Sentiment Pulse", icon: HeartPulse },
    { to: "/admin/ai/prompts", label: "Prompt Library", icon: Library },
    { to: "/admin/ai/usage", label: "AI Cost Dashboard", icon: Coins },
  ],
};

const TOOLS_GROUP = {
  id: "tools", title: "Admin Tools", groupIcon: Settings2,
  items: [
    { to: "/admin/approvals", label: "Approval Queue", icon: UserCircle2 },
    { to: "/admin/pet-approvals", label: "Pet Approvals", icon: PawPrint },
    { to: "/admin/id-verifications", label: "ID Verifications", icon: BadgeCheck },
    { to: "/admin/high-value", label: "High-Value TX", icon: DollarSign },
    { to: "/admin/rfp", label: "RFPs — Private Bids", icon: Briefcase },
    { to: "/admin/customer-envelopes", label: "Customer Envelopes", icon: FileText },
    { to: "/admin/vendor-scorecards", label: "Vendor Scorecards", icon: BadgeCheck },
    { to: "/admin/perks", label: "Perks Marketplace", icon: Sparkles },
    { to: "/admin/board-archive", label: "Board Archive", icon: Archive },
    { to: "/admin/forum-moderation", label: "Forum Moderation", icon: ShieldCheck },
    { to: "/admin/forum-digest", label: "Forum Daily Digest", icon: BellRing },
      { to: "/admin/knowledge-base", label: "Knowledge Base", icon: BookOpen },
    { to: "/admin/sms-consent", label: "SMS Consent", icon: Phone },
    { to: "/admin/api", label: "Public API & Audit", icon: KeySquare },
    { to: "/admin/webhooks", label: "Webhook Rules", icon: Sparkles },
    { to: "/admin/bulk-import", label: "Bulk Import", icon: UploadCloud },
    { to: "/admin/infrastructure", label: "Infrastructure", icon: Server },
    { to: "/admin/status", label: "System Status", icon: Server },
    { to: "/admin/system/crons", label: "Scheduled Tasks", icon: Server },
    { to: "/admin/system/impersonation-log", label: "Impersonation Log", icon: ShieldCheck },
    { to: "/admin/system/password-changes", label: "Password Change Log", icon: KeyRound },
    { to: "/admin/whats-new", label: "What's New", icon: Sparkles },
    { to: "/admin/state-disclosure", label: "State Disclosure Packet", icon: Scale },
    { to: "/admin/email-domains", label: "Email Sender Domains", icon: Mail },
    { to: "/admin/go-live", label: "Go-Live Wizard", icon: BadgeCheck },
    { to: "/admin/settings", label: "Settings", icon: Settings2 },
  ],
};

const ALL_GROUPS = [...NAV_GROUPS, AI_GROUP, TOOLS_GROUP];

/**
 * Role-based sidebar filter. Admins + property managers see everything;
 * the maintenance role gets a tightly scoped view (Overview + Operations only)
 * so a tech who logs in from the front desk can't accidentally touch payroll,
 * governance, or vendor pricing while they triage tickets.
 */
const MAINTENANCE_ALLOWED_GROUPS = new Set(["overview", "operations"]);

function filterGroupsByRole(groups, role) {
  if (role === "maintenance") {
    return groups.filter((g) => MAINTENANCE_ALLOWED_GROUPS.has(g.id));
  }
  return groups;
}



const COLLAPSE_KEY = "admin-nav-collapsed-v2";

function slug(s) {
  return s.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and");
}

function NavItem({ item, isActive, accent = "orange", onNav, theme = "dark" }) {
  const Icon = item.icon;
  const dataTour = (() => {
    const to = item.to || "";
    if (to === "/admin/communities" || to === "/resident/dashboard") return to === "/admin/communities" ? "sidebar-communities" : "sidebar-dashboard";
    if (to === "/admin/finance") return "sidebar-financials";
    if (to === "/admin/dues" || to === "/resident/dues") return "sidebar-dues";
    if (to === "/admin/vendors") return "sidebar-vendors";
    if (to === "/admin/whats-new") return "sidebar-whats-new";
    if (to === "/resident/documents") return "sidebar-documents";
    return undefined;
  })();
  const isDark = theme === "dark";
  const accentText = accent === "indigo"
    ? (isDark ? "text-indigo-400" : "text-indigo-600")
    : (isDark ? "text-orange-400" : "text-orange-600");
  const activeRing = accent === "indigo"
    ? (isDark
        ? "bg-indigo-500/10 text-white shadow-[inset_0_0_0_1px_rgba(99,102,241,0.35)]"
        : "bg-indigo-500/10 text-zinc-900 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.35)]")
    : (isDark
        ? "bg-white/[0.06] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
        : "bg-orange-500/10 text-zinc-900 shadow-[inset_0_0_0_1px_rgba(234,88,12,0.28)]");
  const idle = isDark
    ? "text-zinc-300 hover:bg-white/5 hover:text-white"
    : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900";
  const idleIcon = isDark
    ? "text-zinc-500 group-hover:text-zinc-300"
    : "text-zinc-400 group-hover:text-zinc-700";
  const idleIndigo = isDark ? "text-indigo-400/70" : "text-indigo-500/70";
  return (
    <Link
      to={item.to}
      onClick={onNav}
      data-testid={`nav-${slug(item.label)}`}
      className={
        "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors " +
        (isActive ? activeRing : idle)
      }
    >
      <Icon className={"h-4 w-4 shrink-0 " + (isActive ? accentText : (accent === "indigo" ? idleIndigo : idleIcon))} />
      <span className="truncate">{item.label}</span>
      {isActive && <ChevronRight className={"ml-auto h-3.5 w-3.5 " + accentText} />}
    </Link>
  );
}

function NavGroup({ group, activePath, collapsed, onToggle, query, onNav, theme = "dark" }) {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? group.items.filter((i) => i.label.toLowerCase().includes(q))
    : group.items;
  if (q && filtered.length === 0) return null;
  const groupHasActive = group.items.some((i) => activePath === i.to);
  const isOpen = !collapsed || q || groupHasActive; // auto-expand if searching or has active route
  const GroupIcon = group.groupIcon;
  const isDark = theme === "dark";
  const accentBadge = group.accent === "indigo"
    ? (isDark ? "text-indigo-400" : "text-indigo-600")
    : (isDark ? "text-zinc-500" : "text-zinc-400");
  const headerColor = isDark
    ? "text-zinc-500 hover:text-zinc-300"
    : "text-zinc-500 hover:text-zinc-700";
  const countColor = isDark ? "text-zinc-600" : "text-zinc-400";
  const chevronColor = isDark ? "text-zinc-600" : "text-zinc-400";
  return (
    <div className="mt-4 first:mt-2" data-testid={`sidebar-group-${group.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className={"w-full flex items-center gap-2 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors " + headerColor}
        data-testid={`sidebar-group-toggle-${group.id}`}
        aria-expanded={isOpen}
      >
        <GroupIcon className={"h-3 w-3 " + accentBadge} />
        <span className="font-semibold">{group.title}</span>
        <span className={"ml-1 text-[10px] normal-case tracking-normal " + countColor}>
          {q ? `${filtered.length}` : group.items.length}
        </span>
        <ChevronDown
          className={"h-3 w-3 ml-auto transition-transform " + chevronColor + " " + (isOpen ? "rotate-0" : "-rotate-90")}
        />
      </button>
      {isOpen && (
        <nav className="flex flex-col gap-0.5 px-2 mt-1">
          {filtered.map((item) => (
            <NavItem
              key={item.to}
              item={item}
              isActive={activePath === item.to}
              accent={group.accent}
              onNav={onNav}
              theme={theme}
            />
          ))}
        </nav>
      )}
    </div>
  );
}

export default function AdminShell() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { properties } = useProperty();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch (err) { if (typeof console !== 'undefined') console.debug('components/AdminShell.jsx: swallowed', err); }
  }, [collapsed]);

  // ⌘K / Ctrl+K opens the global command palette. ESC clears sidebar search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === "Escape" && query) setQuery("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [query]);

  // Flat list of routes for the command palette
  const paletteRoutes = useMemo(() => {
    return ALL_GROUPS.flatMap((g) =>
      g.items.map((item) => ({ ...item, group: g.title })),
    );
  }, []);

  const toggleGroup = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  // Fetch caller's effective features (union across all their properties) so
  // we can hide sidebar entries residents/vendors/board members have no grant
  // for. Admins + property_managers bypass this filter entirely (they see the
  // full shell). Fires once on mount + refetches if user identity changes.
  const [effective, setEffective] = useState(null);
  useEffect(() => {
    const skip = !user || user.role === "admin" || user.role === "property_manager";
    if (skip) { setEffective(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/permissions/me");
        if (cancelled) return;
        // Union of base_role_features + all per-property grants
        const set = new Set(data.base_role_features || []);
        Object.values(data.per_property || {}).forEach((arr) => arr.forEach((k) => set.add(k)));
        setEffective(set);
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/AdminShell.jsx: swallowed', err); }
    })();
    return () => { cancelled = true; };
    // We intentionally only re-fetch when the user's identity or role
    // changes, not on every reference to the parent `user` object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  const visibleGroups = useMemo(() => {
    const byRole = filterGroupsByRole(ALL_GROUPS, user?.role);
    return filterGroupsByFeatures(byRole, effective, user?.role);
  }, [user?.role, effective]);

  const totalMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return visibleGroups.reduce(
      (acc, g) => acc + g.items.filter((i) => i.label.toLowerCase().includes(q)).length,
      0,
    );
  }, [query, visibleGroups]);

  return (
    <div className={theme === "dark"
      ? "min-h-screen bg-[#050505] text-white grid-lines-dark"
      : "min-h-screen bg-[#F5F6F7] text-zinc-900"}>
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className={"w-72 shrink-0 flex flex-col " +
          (theme === "dark"
            ? "border-r border-white/10 bg-[#0a0a0b]"
            : "border-r border-black/5 bg-white")} data-testid="admin-sidebar">
          <div className={"p-5 " + (theme === "dark" ? "border-b border-white/10" : "border-b border-black/5")}>
            <div className="flex items-center gap-2 mb-5">
            <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-12 w-12" />
            </div>
            <PropertySwitcher />
          </div>

          {/* Search bar */}
          <div className="px-3 pt-3 pb-1 shrink-0">
            <div className="relative">
              <Search className={"absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none " + (theme === "dark" ? "text-zinc-500" : "text-zinc-400")} />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Jump to… (⌘K)"
                data-testid="sidebar-search-input"
                className={"pl-8 pr-8 h-9 text-sm focus-visible:ring-1 focus-visible:ring-orange-500/40 " +
                  (theme === "dark"
                    ? "bg-white/[0.03] border-white/10 placeholder:text-zinc-600 text-zinc-100"
                    : "bg-zinc-50 border-black/10 placeholder:text-zinc-400 text-zinc-900")}
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className={"absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded " + (theme === "dark" ? "hover:bg-white/10" : "hover:bg-zinc-200")}
                  data-testid="sidebar-search-clear"
                  aria-label="Clear search"
                >
                  <X className={"h-3 w-3 " + (theme === "dark" ? "text-zinc-400" : "text-zinc-500")} />
                </button>
              )}
            </div>
            {totalMatches !== null && (
              <div className={"text-[10px] mt-1.5 px-1 uppercase tracking-widest " + (theme === "dark" ? "text-zinc-500" : "text-zinc-400")} data-testid="sidebar-search-count">
                {totalMatches} {totalMatches === 1 ? "match" : "matches"}
              </div>
            )}
          </div>

          <div className="overflow-y-auto flex-1 pb-6 scroll-slim">
            {visibleGroups.map((group) => (
              <NavGroup
                key={group.id}
                group={group}
                activePath={pathname}
                collapsed={!!collapsed[group.id]}
                onToggle={() => toggleGroup(group.id)}
                query={query}
                onNav={() => query && setQuery("")}
                theme={theme}
              />
            ))}
            {query && totalMatches === 0 && (
              <div className={"px-5 py-6 text-center text-xs " + (theme === "dark" ? "text-zinc-500" : "text-zinc-400")} data-testid="sidebar-search-empty">
                No admin page matches “{query}”.
              </div>
            )}
          </div>

          <div className={"p-3 " + (theme === "dark" ? "border-t border-white/10" : "border-t border-black/5")}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={"w-full flex items-center gap-3 rounded-md p-2 transition-colors " + (theme === "dark" ? "hover:bg-white/5" : "hover:bg-zinc-100")} data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar || ""} alt={user?.name || ""} />
                    <AvatarFallback className="bg-orange-500/20 text-orange-500 dark:text-orange-300">
                      {(user?.name || "A").slice(0,1)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left min-w-0 flex-1">
                    <div className={"text-sm font-medium truncate " + (theme === "dark" ? "text-white" : "text-zinc-900")}>{user?.email}</div>
                    <div className={"text-[11px] uppercase tracking-widest " + (theme === "dark" ? "text-zinc-500" : "text-zinc-400")}>{user?.role || "admin"}</div>
                  </div>
                  <ChevronsUpDown className={"h-4 w-4 " + (theme === "dark" ? "text-zinc-500" : "text-zinc-400")} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-56">
                <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setAiOpen(true)} data-testid="menu-open-ai">
                  <Sparkles className="h-4 w-4 mr-2" />Open AI Concierge
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 flex flex-col">
          <header className={"h-14 flex items-center justify-between px-6 sticky top-0 z-30 backdrop-blur-md " +
            (theme === "dark"
              ? "border-b border-white/10 bg-[#0a0a0b]/70"
              : "border-b border-black/5 bg-white/70")} data-testid="topbar">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="border-orange-500/40 text-orange-500 dark:text-orange-300 bg-orange-500/10">Admin Console</Badge>
              <div className={"text-sm hidden md:block " + (theme === "dark" ? "text-zinc-400" : "text-zinc-600")}>
                {properties.length} propert{properties.length === 1 ? "y" : "ies"} under management
              </div>
              <LiveIndicator />
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className={"hidden md:inline-flex rounded-full text-xs gap-1.5 pl-2 pr-2.5 " +
                  (theme === "dark"
                    ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-zinc-300 hover:text-white"
                    : "border-black/10 bg-white hover:bg-zinc-100 text-zinc-700")}
                onClick={() => setPaletteOpen(true)}
                data-testid="topbar-command-palette"
              >
                <Command className="h-3.5 w-3.5" /> Search
                <span className={"ml-1 text-[10px] px-1.5 py-0.5 rounded font-mono " +
                  (theme === "dark" ? "border border-white/10 text-zinc-400" : "border border-black/10 text-zinc-500")}>⌘K</span>
              </Button>
              <ThemeToggle variant={theme === "dark" ? "default" : "light"} />
              <LiveNotificationsBell onClick={() => setNotifOpen(true)} />
              <Button
                size="sm"
                variant="secondary"
                className="bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-200 border border-indigo-500/30 rounded-full"
                onClick={() => setAiOpen(true)}
                data-testid="topbar-ai-button"
              >
                <Sparkles className="h-4 w-4 mr-1.5" /> AI Concierge
              </Button>
            </div>
          </header>
          <AnimatePresence mode="wait">
            <motion.div
              key={pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="p-8"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent className="glass-dark border-l border-white/10 w-full sm:max-w-md p-0 flex flex-col h-full">
          <SheetHeader className="p-4 border-b border-white/10 shrink-0">
            <SheetTitle className="text-white font-display">AI Concierge</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            <AIConciergeDrawer agent="concierge" theme="dark" />
          </div>
        </SheetContent>
      </Sheet>

      <NotificationDrawer open={notifOpen} onOpenChange={setNotifOpen} />

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        routes={paletteRoutes}
      />
    </div>
  );
}
